import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import { FileText, Filter, X } from 'lucide-react';

interface Veiculo {
  id: number;
  placa: string;
  prefixo?: string | null;
  marca?: string | null;
  modelo?: string | null;
}

interface Motorista {
  id: number;
  nome: string;
  matricula?: string | null;
}

interface CMV {
  id: number;
  cmv: number;
  data: string;
  polo: string;
  turno: string;
  veiculo_id: number;
  veiculo_placa: string;
  veiculo_prefixo?: string | null;
  veiculo_marca?: string | null;
  veiculo_modelo?: string | null;
  motorista_id: number;
  motorista_nome: string;
  motorista_matricula?: string | null;
  criado_em: string;
}

const CMV: React.FC = () => {
  const { user } = useAuthStore();
  const [cmvs, setCmvs] = useState<CMV[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingCMVId, setEditingCMVId] = useState<number | null>(null);
  
  // Estados para busca de veículos e motoristas
  const [veiculoSearch, setVeiculoSearch] = useState('');
  const [veiculosOptions, setVeiculosOptions] = useState<Veiculo[]>([]);
  const [motoristaSearch, setMotoristaSearch] = useState('');
  const [motoristasOptions, setMotoristasOptions] = useState<Motorista[]>([]);
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    data: '',
    turno: '',
    polo: ''
  });

  const [formData, setFormData] = useState({
    id_veiculo: '',
    id_motorista: '',
    data: '',
    polo: '',
    turno: ''
  });
  const [editFormData, setEditFormData] = useState({
    id_veiculo: '',
    id_motorista: '',
    data: '',
    polo: '',
    turno: ''
  });

  useEffect(() => {
    if (!user || user.funcao !== 'admin') {
      toast.error('Acesso negado. Apenas administradores podem gerenciar CMV.');
      return;
    }
    
    fetchCMVs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filters]);

  // Buscar veículos quando o termo de busca mudar
  useEffect(() => {
    if (veiculoSearch.length >= 2) {
      const timeoutId = setTimeout(() => {
        searchVeiculos(veiculoSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setVeiculosOptions([]);
    }
  }, [veiculoSearch]);

  // Buscar motoristas quando o termo de busca mudar
  useEffect(() => {
    if (motoristaSearch.length >= 2) {
      const timeoutId = setTimeout(() => {
        searchMotoristas(motoristaSearch);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setMotoristasOptions([]);
    }
  }, [motoristaSearch]);

  const searchVeiculos = async (term: string) => {
    try {
      const response = await api.get(`/veiculos?search=${encodeURIComponent(term)}&limit=10`);
      setVeiculosOptions(response.data?.veiculos || []);
    } catch (error) {
      console.error('Erro ao buscar veículos:', error);
    }
  };

  const searchMotoristas = async (term: string) => {
    try {
      const response = await api.get(`/usuarios/motoristas?search=${encodeURIComponent(term)}`);
      setMotoristasOptions(response.data?.motoristas || []);
    } catch (error) {
      console.error('Erro ao buscar motoristas:', error);
    }
  };

  const fetchCMVs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.data) {
        params.append('data', filters.data);
      }
      if (filters.turno) {
        params.append('turno', filters.turno);
      }
      if (filters.polo) {
        params.append('polo', filters.polo);
      }

      const queryString = params.toString();
      const url = queryString ? `/cmv?${queryString}` : '/cmv';
      
      const response = await api.get(url);
      const cmvsData = response.data?.cmvs || [];
      setCmvs(cmvsData);
    } catch (error: any) {
      console.error('❌ Erro ao carregar CMVs:', error);
      if (error.response?.status === 401) {
        toast.error('Não autorizado. Faça login novamente.');
      } else if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores podem acessar esta página.');
      } else {
        toast.error(`Erro ao carregar CMVs: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCMV = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.id_veiculo || !formData.id_motorista) {
      toast.error('Por favor, selecione um veículo e um motorista');
      return;
    }
    
    try {
      const dadosEnvio = {
        id_veiculo: parseInt(formData.id_veiculo),
        id_motorista: parseInt(formData.id_motorista),
        data: formData.data,
        polo: formData.polo,
        turno: formData.turno
      };

      const response = await api.post('/cmv', dadosEnvio);
      toast.success('CMV criado com sucesso!');
      setShowCreateForm(false);
      resetFormData();
      fetchCMVs();
    } catch (error: any) {
      console.error('❌ Erro ao criar CMV:', error);
      if (error.response?.status === 400) {
        const errorMsg = error.response.data?.error || 'Dados inválidos';
        toast.error(`Erro: ${errorMsg}`);
      } else {
        toast.error(`Erro ao criar CMV: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const handleEditCMV = async (cmvId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/cmv/${cmvId}`);
      const cmv = response.data.cmv;
      
      // Processar data corretamente (evitar problemas de timezone)
      let dataFormatada = '';
      if (cmv.data) {
        if (typeof cmv.data === 'string') {
          // Se já é string no formato YYYY-MM-DD, usar diretamente
          dataFormatada = cmv.data.split('T')[0];
        } else {
          // Se é objeto Date, converter para string local (não UTC)
          const date = new Date(cmv.data);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          dataFormatada = `${year}-${month}-${day}`;
        }
      }
      
      setEditFormData({
        id_veiculo: cmv.id_veiculo?.toString() || cmv.veiculo_id?.toString() || '',
        id_motorista: cmv.id_motorista?.toString() || cmv.motorista_id?.toString() || '',
        data: dataFormatada,
        polo: cmv.polo || '',
        turno: cmv.turno || ''
      });
      
      // Preencher busca com dados atuais
      setVeiculoSearch(`${cmv.veiculo_placa}${cmv.veiculo_prefixo ? ` - ${cmv.veiculo_prefixo}` : ''}`);
      setMotoristaSearch(`${cmv.motorista_nome}${cmv.motorista_matricula ? ` - ${cmv.motorista_matricula}` : ''}`);
      
      setEditingCMVId(cmvId);
      setShowEditForm(true);
      setShowCreateForm(false);
    } catch (error: any) {
      console.error('❌ Erro ao buscar CMV:', error);
      if (error.response?.status === 404) {
        toast.error('CMV não encontrado');
      } else {
        toast.error(`Erro ao carregar dados do CMV: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCMV = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCMVId) return;

    try {
      const updateData: any = {};
      // Sempre enviar id_veiculo e id_motorista se estiverem preenchidos
      if (editFormData.id_veiculo) {
        updateData.id_veiculo = parseInt(editFormData.id_veiculo);
      }
      if (editFormData.id_motorista) {
        updateData.id_motorista = parseInt(editFormData.id_motorista);
      }
      if (editFormData.data) updateData.data = editFormData.data;
      if (editFormData.polo) updateData.polo = editFormData.polo;
      if (editFormData.turno) updateData.turno = editFormData.turno;

      const response = await api.put(`/cmv/${editingCMVId}`, updateData);
      toast.success('CMV atualizado com sucesso!');
      setShowEditForm(false);
      setEditingCMVId(null);
      resetEditFormData();
      fetchCMVs();
    } catch (error: any) {
      console.error('❌ Erro ao atualizar CMV:', error);
      if (error.response?.status === 400) {
        const errorMsg = error.response.data?.error || 'Dados inválidos';
        toast.error(`Erro: ${errorMsg}`);
      } else {
        toast.error(`Erro ao atualizar CMV: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const handleDeleteCMV = async (cmvId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este CMV?')) {
      return;
    }

    try {
      await api.delete(`/cmv/${cmvId}`);
      toast.success('CMV excluído com sucesso!');
      fetchCMVs();
    } catch (error: any) {
      console.error('❌ Erro ao excluir CMV:', error);
      toast.error(`Erro ao excluir CMV: ${error.response?.data?.error || error.message}`);
    }
  };

  const resetFormData = () => {
    setFormData({
      id_veiculo: '',
      id_motorista: '',
      data: '',
      polo: '',
      turno: ''
    });
    setVeiculoSearch('');
    setMotoristaSearch('');
    setVeiculosOptions([]);
    setMotoristasOptions([]);
  };

  const resetEditFormData = () => {
    setEditFormData({
      id_veiculo: '',
      id_motorista: '',
      data: '',
      polo: '',
      turno: ''
    });
    setVeiculoSearch('');
    setMotoristaSearch('');
    setVeiculosOptions([]);
    setMotoristasOptions([]);
  };

  const resetFilters = () => {
    setFilters({
      data: '',
      turno: '',
      polo: ''
    });
  };

  const hasActiveFilters = () => {
    return filters.data !== '' || filters.turno !== '' || filters.polo !== '';
  };

  if (loading && cmvs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">CMV - Controle de Movimentação de Veículos</h1>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setShowEditForm(false);
            setEditingCMVId(null);
            resetFormData();
          }}
          className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <FileText className="h-5 w-5" />
          Novo CMV
        </button>
      </div>

      {/* Formulário de edição */}
      {showEditForm && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-semibold mb-4">Editar CMV</h2>
          <form onSubmit={handleUpdateCMV} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Veículo *</label>
                <input
                  type="text"
                  value={veiculoSearch}
                  onChange={(e) => {
                    setVeiculoSearch(e.target.value);
                    if (e.target.value.length < 2) {
                      setEditFormData({...editFormData, id_veiculo: ''});
                    }
                  }}
                  placeholder="Buscar por placa ou prefixo..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {veiculosOptions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {veiculosOptions.map((veiculo) => (
                      <div
                        key={veiculo.id}
                        onClick={() => {
                          setEditFormData({...editFormData, id_veiculo: veiculo.id.toString()});
                          setVeiculoSearch(`${veiculo.placa}${veiculo.prefixo ? ` - ${veiculo.prefixo}` : ''}`);
                          setVeiculosOptions([]);
                        }}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                      >
                        {veiculo.placa}{veiculo.prefixo ? ` - ${veiculo.prefixo}` : ''}
                        {veiculo.marca && veiculo.modelo && ` (${veiculo.marca} ${veiculo.modelo})`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Motorista *</label>
                <input
                  type="text"
                  value={motoristaSearch}
                  onChange={(e) => {
                    setMotoristaSearch(e.target.value);
                    if (e.target.value.length < 2) {
                      setEditFormData({...editFormData, id_motorista: ''});
                    }
                  }}
                  placeholder="Buscar por nome ou matrícula..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {motoristasOptions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {motoristasOptions.map((motorista) => (
                      <div
                        key={motorista.id}
                        onClick={() => {
                          setEditFormData({...editFormData, id_motorista: motorista.id.toString()});
                          setMotoristaSearch(`${motorista.nome}${motorista.matricula ? ` - ${motorista.matricula}` : ''}`);
                          setMotoristasOptions([]);
                        }}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                      >
                        {motorista.nome}{motorista.matricula ? ` - ${motorista.matricula}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data *</label>
                <input
                  type="date"
                  value={editFormData.data}
                  onChange={(e) => setEditFormData({...editFormData, data: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Polo *</label>
                <select
                  value={editFormData.polo}
                  onChange={(e) => setEditFormData({...editFormData, polo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecione</option>
                  <option value="Vila Mariana">Vila Mariana</option>
                  <option value="Tatuapé">Tatuapé</option>
                  <option value="Vila Formosa">Vila Formosa</option>
                  <option value="Santana">Santana</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Turno *</label>
                <select
                  value={editFormData.turno}
                  onChange={(e) => setEditFormData({...editFormData, turno: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecione</option>
                  <option value="Manhã - 12x36 - 06hrs as 18hrs">Manhã - 12x36 - 06hrs as 18hrs</option>
                  <option value="Noite - 12x36 - 18hrs as 06hrs">Noite - 12x36 - 18hrs as 06hrs</option>
                </select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Salvar Alterações
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowEditForm(false);
                  setEditingCMVId(null);
                  resetEditFormData();
                }}
                className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulário de criação */}
      {showCreateForm && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-semibold mb-4">Cadastrar CMV</h2>
          <form onSubmit={handleCreateCMV} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Veículo *</label>
                <input
                  type="text"
                  value={veiculoSearch}
                  onChange={(e) => {
                    setVeiculoSearch(e.target.value);
                    if (e.target.value.length < 2) {
                      setFormData({...formData, id_veiculo: ''});
                    }
                  }}
                  placeholder="Buscar por placa ou prefixo..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {veiculosOptions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {veiculosOptions.map((veiculo) => (
                      <div
                        key={veiculo.id}
                        onClick={() => {
                          setFormData({...formData, id_veiculo: veiculo.id.toString()});
                          setVeiculoSearch(`${veiculo.placa}${veiculo.prefixo ? ` - ${veiculo.prefixo}` : ''}`);
                          setVeiculosOptions([]);
                        }}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                      >
                        {veiculo.placa}{veiculo.prefixo ? ` - ${veiculo.prefixo}` : ''}
                        {veiculo.marca && veiculo.modelo && ` (${veiculo.marca} ${veiculo.modelo})`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Motorista *</label>
                <input
                  type="text"
                  value={motoristaSearch}
                  onChange={(e) => {
                    setMotoristaSearch(e.target.value);
                    if (e.target.value.length < 2) {
                      setFormData({...formData, id_motorista: ''});
                    }
                  }}
                  placeholder="Buscar por nome ou matrícula..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {motoristasOptions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {motoristasOptions.map((motorista) => (
                      <div
                        key={motorista.id}
                        onClick={() => {
                          setFormData({...formData, id_motorista: motorista.id.toString()});
                          setMotoristaSearch(`${motorista.nome}${motorista.matricula ? ` - ${motorista.matricula}` : ''}`);
                          setMotoristasOptions([]);
                        }}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                      >
                        {motorista.nome}{motorista.matricula ? ` - ${motorista.matricula}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data *</label>
                <input
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({...formData, data: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Polo *</label>
                <select
                  value={formData.polo}
                  onChange={(e) => setFormData({...formData, polo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecione</option>
                  <option value="Vila Mariana">Vila Mariana</option>
                  <option value="Tatuapé">Tatuapé</option>
                  <option value="Vila Formosa">Vila Formosa</option>
                  <option value="Santana">Santana</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Turno *</label>
                <select
                  value={formData.turno}
                  onChange={(e) => setFormData({...formData, turno: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Selecione</option>
                  <option value="Manhã - 12x36 - 06hrs as 18hrs">Manhã - 12x36 - 06hrs as 18hrs</option>
                  <option value="Noite - 12x36 - 18hrs as 06hrs">Noite - 12x36 - 18hrs as 06hrs</option>
                </select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                className="w-full sm:w-auto bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Cadastrar
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetFormData();
                }}
                className="w-full sm:w-auto bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data</label>
            <input
              type="date"
              value={filters.data}
              onChange={(e) => setFilters({...filters, data: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Turno</label>
            <select
              value={filters.turno}
              onChange={(e) => setFilters({...filters, turno: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="Manhã - 12x36 - 06hrs as 18hrs">Manhã - 12x36 - 06hrs as 18hrs</option>
              <option value="Noite - 12x36 - 18hrs as 06hrs">Noite - 12x36 - 18hrs as 06hrs</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Polo</label>
            <select
              value={filters.polo}
              onChange={(e) => setFilters({...filters, polo: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="Vila Mariana">Vila Mariana</option>
              <option value="Tatuapé">Tatuapé</option>
              <option value="Vila Formosa">Vila Formosa</option>
              <option value="Santana">Santana</option>
            </select>
          </div>
        </div>
        {hasActiveFilters() && (
          <div className="mt-4">
            <button
              onClick={resetFilters}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <X className="h-4 w-4" />
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Lista de CMVs */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CMV</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Veículo</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Motorista</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Polo</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Turno</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cmvs.map((cmv) => (
                <tr key={cmv.id} className="hover:bg-gray-50">
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{cmv.cmv}</td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cmv.veiculo_placa}{cmv.veiculo_prefixo ? ` - ${cmv.veiculo_prefixo}` : ''}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {cmv.motorista_nome}{cmv.motorista_matricula ? ` - ${cmv.motorista_matricula}` : ''}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(cmv.data).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cmv.polo}</td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cmv.turno}</td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleEditCMV(cmv.id)}
                        className="text-blue-600 hover:text-blue-900 text-xs sm:text-sm"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteCMV(cmv.id)}
                        className="text-red-600 hover:text-red-900 text-xs sm:text-sm"
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {cmvs.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhum CMV cadastrado
          </div>
        )}
      </div>
    </div>
  );
};

export default CMV;

